import { Bot, User, HelpCircle, Pencil, Send } from 'lucide-react';
import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import clsx from 'clsx';
import { ProviderCard, ProviderDetailModal } from '@/components/providers';
import { useModalStore } from '@/stores';
import type { Message, ProviderOption } from '@/types/chat';
import { fromProviderOption } from '@/types/provider-card';
import { QuotationTable } from './QuotationTable';
import styles from '@/pages/Dashboard/Chat.module.css';

interface MessageBubbleProps {
  /** The chat message to render. */
  message: Message;
  /**
   * Called when the user clicks a pending-question option button.
   * The option text is passed as the message content.
   * Optional — if omitted, option buttons are not rendered.
   */
  onSendMessage?: (content: string) => void;
  /** Whether a send is currently in flight; disables option buttons when true. */
  isSending?: boolean;
  /**
   * Called when the user right-clicks the message row.
   * Receives the native MouseEvent and the message UUID.
   * Only provided for persisted messages (those with an `id`).
   */
  onMessageContextMenu?: (e: React.MouseEvent, messageId: string) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  onSendMessage,
  isSending = false,
  onMessageContextMenu,
}) => {
  const isBot = message.role === 'assistant';
  const { openModal } = useModalStore();

  const [freeTextOpen, setFreeTextOpen] = useState(false);
  const [freeText, setFreeText] = useState('');
  const freeTextRef = useRef<HTMLTextAreaElement>(null);

  const openFreeText = () => {
    setFreeTextOpen(true);
    setTimeout(() => freeTextRef.current?.focus(), 50);
  };

  const submitFreeText = () => {
    const trimmed = freeText.trim();
    if (!trimmed || isSending || !onSendMessage) return;
    onSendMessage(trimmed);
    setFreeText('');
    setFreeTextOpen(false);
  };

  const viewOptionDetails = (opt: ProviderOption) => {
    openModal(<ProviderDetailModal provider={fromProviderOption(opt)} />);
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    if (!message.id || !onMessageContextMenu) return;
    e.preventDefault();
    onMessageContextMenu(e, message.id);
  };

  return (
    <div
      className={clsx(styles.messageRow, isBot ? styles.bot : styles.user)}
      onContextMenu={message.id && onMessageContextMenu ? handleContextMenu : undefined}
    >
      <div className={styles.messageContentWrapper}>
        {/* Avatar */}
        <div className={clsx(styles.avatar, isBot ? styles.bot : styles.user)}>
          {isBot ? <Bot size={16} /> : <User size={16} />}
        </div>

        <div className={styles.messageContent}>
          {/* Content Bubble */}
          <div className={clsx(styles.bubble, isBot ? styles.bot : styles.user)}>
            {isBot ? (
              <div className={styles.markdownContent}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
              </div>
            ) : (
              <p className={styles.userText}>{message.content}</p>
            )}
          </div>

          {/* Quotation Table (Assistant only) */}
          {isBot && message.quotation && <QuotationTable quotation={message.quotation} />}

          {/* Provider Option Cards (Assistant only) */}
          {isBot && message.options && message.options.length > 0 && (
            <div className={styles.optionsWrapper}>
              <p className={styles.optionsLabel}>Options Found</p>
              <div className={styles.cardsContainer}>
                {message.options.map((opt) => (
                  <ProviderCard
                    key={opt.id}
                    provider={fromProviderOption(opt)}
                    mode="compact"
                    onViewDetails={() => viewOptionDetails(opt)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Pending clarifying question (Assistant only) */}
          {isBot && message.pendingQuestion && (
            <div className={styles.pendingQuestion}>
              <div className={styles.pendingQuestionHeader}>
                <HelpCircle size={14} />
                <span>Quick question</span>
              </div>
              <p className={styles.pendingQuestionText}>{message.pendingQuestion}</p>
              {message.pendingQuestionOptions && message.pendingQuestionOptions.length > 0 && (
                <div className={styles.pendingOptions}>
                  {message.pendingQuestionOptions.map((opt, optIdx) => (
                    <button
                      key={optIdx}
                      type="button"
                      className={styles.pendingOption}
                      onClick={() => onSendMessage?.(opt)}
                      disabled={isSending || !onSendMessage}
                    >
                      <span className={styles.pendingOptionNum}>{optIdx + 1}</span>
                      <span className={styles.pendingOptionLabel}>{opt}</span>
                    </button>
                  ))}

                  {/* Free-text fallback */}
                  {!freeTextOpen ? (
                    <button
                      type="button"
                      className={styles.freeAnswerBtn}
                      onClick={openFreeText}
                      disabled={isSending || !onSendMessage}
                    >
                      <Pencil size={12} />
                      <span>Or write your own answer...</span>
                    </button>
                  ) : (
                    <div className={styles.freeAnswerRow}>
                      <textarea
                        ref={freeTextRef}
                        className={styles.freeAnswerInput}
                        rows={2}
                        placeholder="Type your answer and press Send..."
                        value={freeText}
                        onChange={(e) => setFreeText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                            e.preventDefault();
                            submitFreeText();
                          }
                          if (e.key === 'Escape') {
                            setFreeTextOpen(false);
                            setFreeText('');
                          }
                        }}
                        disabled={isSending}
                      />
                      <button
                        type="button"
                        className={styles.freeAnswerSend}
                        onClick={submitFreeText}
                        disabled={!freeText.trim() || isSending || !onSendMessage}
                        aria-label="Send free answer"
                      >
                        <Send size={14} />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
